import React, { useState, createRef, useCallback, useEffect } from "react";
import { get as _get } from "lodash-es";
import ReactAvatarEditor from "react-avatar-editor";
import Dropzone from "react-dropzone";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

import { IMG_SIZE } from "./Avatar/styled";
import { axiosClient } from "../redux_axios_middleware";
import { Modal, ModalBody, ModalFooter, ModalTitle } from "./Modal";
import Button from "./Button";
import { updateUserLocal } from "../actions/user";

const UserImageEditModal = ({ onClosed }) => {
  const dispatch = useDispatch();
  const [scale, setScale] = useState(1.0);

  const [image, setImage] = useState(null);
  const dropzoneRef = createRef();
  const imageRef = createRef();

  const openDialog = () => {
    // Note that the ref is set async,
    // so it might be null at some point
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };

  const onPaste = useCallback((event) => {
    const items = (event.clipboardData || event.originalEvent.clipboardData)
      .items;
    for (let index in items) {
      const item = items[index];
      if (item.kind === "file") {
        setImage(item.getAsFile());
        return;
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("paste", onPaste);
    };
  }, [onPaste]);

  const onSaveImage = useCallback(() => {
    imageRef.current.getImageScaledToCanvas().toBlob(
      function (blob) {
        const formData = new FormData();
        formData.append("image", blob);

        axiosClient({
          method: "POST",
          url: "user-photo.json",
          data: formData,
        })
          .then((response) => {
            dispatch(updateUserLocal({ photo: response.data.image }));
            onClosed();
          })
          .catch((error) => {
            const errorMessage = _get(error, "response.data.error");
            if (errorMessage) {
              alert(errorMessage);
            } else {
              alert(`Could not upload the image. ${error}`);
            }
          });
      },
      "image/jpeg",
      0.98,
    );
  }, [imageRef, onClosed, dispatch]);

  const onDeleteCurrentImage = useCallback(() => {
    axiosClient({
      method: "DELETE",
      url: "user-photo.json",
    })
      .then(() => {
        dispatch(updateUserLocal({ photo: undefined }));
        onClosed();
      })
      .catch((error) => {
        alert(`Could not delete the image. ${error}`);
      });
  }, [onClosed, dispatch]);

  const img_size = IMG_SIZE * 1.5; // hidpi

  return (
    <Modal isOpened={true} onClose={onClosed}>
      <ModalTitle>Change image</ModalTitle>
      <ModalBody>
        {" "}
        <Dropzone
          ref={dropzoneRef}
          multiple={false}
          accept="image/*"
          onDrop={(file) => setImage(file[0])}
          noClick // to keep image dragable in the editor
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              {image ? (
                <ReactAvatarEditor
                  ref={imageRef}
                  width={img_size}
                  height={img_size}
                  borderRadius={img_size / 2}
                  image={image}
                  scale={scale}
                />
              ) : null}

              {image && (
                <div>
                  Zoom:{" "}
                  <input
                    type="range"
                    step="0.01"
                    min="1"
                    max="3"
                    value={scale}
                    onChange={(e) => {
                      setScale(parseFloat(e.target.value));
                    }}
                  />
                </div>
              )}

              <div>
                <Button onClick={openDialog}>
                  {image ? "Choose a different file" : "Choose a file"}
                </Button>{" "}
                or paste an image
              </div>
            </div>
          )}
        </Dropzone>
      </ModalBody>
      <ModalFooter>
        <div className="mt-5">
          <Button
            className="mr-3"
            intent="danger"
            onClick={onDeleteCurrentImage}
          >
            Delete current image
          </Button>{" "}
          <Button onClick={onSaveImage} disabled={!image}>
            Save
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

UserImageEditModal.propTypes = {
  onClosed: PropTypes.func,
};

export default UserImageEditModal;
